import { makeAutoObservable } from "mobx";
import {
  AuditorListItem,
  DbotEvent,
  DecisionContext,
  DbotEventDispatcher,
} from "../common/commonDomain";

import { emailValidator, failure, ok, pending, Result } from "../../common/common";

import { TMessage } from "../../i18n/tt";
import { DecisionType } from "../../common/decisions";
import { StringFieldModel } from "../../common/commonForms";

export type ChangeOfAuditor = {
  type: DecisionType.auditor;
  name?: string;
  email?: string;
  organisationNumber?: string;
  businessAddress1?: string;
  businessAddress2?: string;
  businessZip?: string;
  businessCity?: string;
  businessCountry?: string;
};

export type AuditorsResult = Result<AuditorListItem[], TMessage>;

export class ChangeOfAuditorForm {
  readonly type = DecisionType.auditor;
  readonly dispatch: DbotEventDispatcher;
  readonly context: DecisionContext;
  readonly currentAuditor?: AuditorListItem;
  readonly data?: ChangeOfAuditor;

  selectedAuditor?: AuditorListItem;
  emailModel: StringFieldModel;
  auditors: AuditorsResult = pending();

  validationError?: TMessage;

  constructor(dispatch: DbotEventDispatcher, context: DecisionContext, data?: ChangeOfAuditor) {
    makeAutoObservable(this, { type: false, dispatch: false, data: false }, { autoBind: true });

    this.dispatch = dispatch;
    this.context = context;
    this.currentAuditor = getCurrentAuditor(context);
    this.data = data;
    this.selectedAuditor = mapChangeOfAuditorToListItem(data);
    this.emailModel = new StringFieldModel({
      required: true,
      value: data?.email ?? "",
      validator: emailValidator,
    });
  }

  init(): void {
    this.dispatch({ type: "dbot/fetch-auditors" });
  }

  handleEvent(event: DbotEvent): void {
    switch (event.type) {
      case "dbot/fetch-auditors-success":
        this.auditors = ok(event.data.items);
        break;
      case "dbot/fetch-auditors-error":
        this.auditors = failure(event.error);
        break;
    }
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return this.emailModel.isValid && this.selectedAuditor !== undefined;
  }

  validate(): boolean {
    this.emailModel.validate();
    this.validationError = this.selectedAuditor !== undefined ? undefined : { key: "required" };
    return this.isValid;
  }

  setSelectedAuditor(auditor: AuditorListItem) {
    this.selectedAuditor = auditor;
    this.validationError = undefined;
  }

  clearSelectedAuditor() {
    this.selectedAuditor = undefined;
    this.validationError = undefined;
  }
}

export function mapChangeOfAuditorToListItem(data?: ChangeOfAuditor): AuditorListItem | undefined {
  return data && data.name && data.organisationNumber
    ? {
        name: data.name,
        organisationNumber: data.organisationNumber,
        businessAddress1: data.businessAddress1,
        businessAddress2: data.businessAddress2,
        businessZip: data.businessZip,
        businessCity: data.businessCity,
        businessCountry: data.businessCountry,
      }
    : undefined;
}

export function getCurrentAuditor(decisionContext: DecisionContext): AuditorListItem | undefined {
  const name = decisionContext.companyInfo?.auditorName;
  const organisationNumber = decisionContext.companyInfo?.company.auditorCompanyOrganizationnumber;
  if (name && organisationNumber) {
    return { name, organisationNumber };
  }
}
